import { useEffect, useRef, useCallback, useState } from 'react';
import { getAuthToken } from '@/lib/tokenStorage';

// iter201 — Module-level reference to the active WebSocket. Exposed
// via `sendWsEvent()` so any component (annotation typing chip,
// presence indicators, etc.) can push events to teammates without
// opening a second socket. Cleared automatically on close.
let _activeWs = null;

export const sendWsEvent = (event, data) => {
  if (!_activeWs || _activeWs.readyState !== WebSocket.OPEN) return false;
  try {
    _activeWs.send(JSON.stringify({ event, data: data || {} }));
    return true;
  } catch {
    return false;
  }
};

const useWebSocket = (onEvent) => {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const onEventRef = useRef(onEvent);
  const [connected, setConnected] = useState(false);

  // Keep callback ref current without triggering reconnects
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const connect = useCallback(() => {
    const token = getAuthToken();
    if (!token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const url = `${protocol}//${host}/api/ws?token=${token}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      _activeWs = ws;  // iter201 — expose for module-level sendWsEvent

      ws.onopen = () => {
        setConnected(true);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          // iter85 — WebSocket toast noise removed. The bell badge + native
          // browser notification already surface these events; the extra
          // toasts stacked with each other whenever multiple WS events
          // fired in quick succession (esp. right after a bell-click page
          // reload replayed backlog events). Delegate any UI reaction to
          // subscribers via `onEventRef.current`.
          if (onEventRef.current) onEventRef.current(message);
          // iter198 — also dispatch onto a window CustomEvent so hooks
          // like `useAnnotations` can subscribe to real-time team-thread
          // events without opening a second WebSocket connection.
          try {
            window.dispatchEvent(new CustomEvent('dotbox:ws', { detail: message }));
          } catch { /* older browsers — ignore */ }
        } catch (err) {
          // Non-JSON messages (e.g. server-side "pong" heartbeats) are expected and safe to skip
          if (err instanceof SyntaxError) {
            // ignore
          } else {
            console.error('WebSocket message handler failed:', err);
          }
        }
      };

      ws.onclose = () => {
        setConnected(false);
        if (_activeWs === ws) _activeWs = null;
        wsRef.current = null;
        reconnectTimer.current = setTimeout(connect, 10000);
      };

      ws.onerror = () => {
        // Will trigger onclose
      };

      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      ws._pingInterval = pingInterval;
    } catch (err) {
      console.error('WebSocket connection failed:', err);
      reconnectTimer.current = setTimeout(connect, 10000);
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        if (_activeWs === wsRef.current) _activeWs = null;
        wsRef.current.close();
      }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  return { connected };
};

export default useWebSocket;
